import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, DesktopPagination, EmptyState } from '../common';
import V2DateTime from '../common/V2DateTime';
import { useDesktopExpanded } from '../../hooks/useDesktopExpanded';
import { fetchOcrProjects, getProjectStatusDisplay } from './ocrApi';
import ProjectCreateModal from './ProjectCreateModal';

/**
 * OCR(/ocr-hub) 좌측 마스터 — 프로젝트 목록 (WorkListTab 목록 관례 이식).
 *
 * 행 클릭 → onSelect(project_id) → /ocr-hub/project/{id} (우측 디테일).
 * per_page는 viewport 높이 기반 계산(WorkListTab 관례, 계약 범위 5~100 클램프).
 * 백엔드 미배포·네트워크 실패는 빈 목록(EmptyState)으로 흡수한다 — 401은 apiUtils가 /signin 처리.
 *
 * @param {boolean} compact  좌우 분할 시 폭 축소 모드(컬럼 축약)
 * @param {string|null} selected  선택된 project_id (행 강조용)
 * @param {(projectId: number|string) => void} onSelect  행 클릭 콜백
 */
// 목록 API 는 상태 필터를 받지 않는다(GET /projects?page=&per_page= 뿐). 그래서 화면이 거른다.
// 서버 페이지를 그대로 두고 '보이는 페이지 안에서만' 거르면 「종료 3건」이 실제로는 12건일 수 있어
// 배지가 거짓말을 한다. 전량을 한 번 받아 두고 화면에서 거르고 나눈다.
const FETCH_PAGE_SIZE = 100; // 계약 per_page 상한
const FETCH_MAX_PAGES = 10;  // 1000건까지. 넘으면 truncated 로 알린다(조용히 자르지 않는다)

// 칩은 표의 상태 배지와 **같은 색**을 쓴다(2026-08-23 오너 결정). 그래서 「지금 고른 것」을
// 색으로 말할 수 없다 — 색은 이미 「어느 상태인가」를 나르고 있고, 색만으로 위계를 만들면
// 색을 못 보는 사람에게 선택이 사라진다(rules/visual-hierarchy §2).
// 선택 표시는 **굵기 하나**로 준다(같은 날 오너 지시로 테두리를 걷어냈다). 색을 못 보는
// 사람에게도 남는 축이고, 무엇을 골랐는지는 목록 자체가 함께 바뀌어 말한다.
const CHIP_ON = 'font-bold';
const CHIP_OFF = 'font-medium';
// 「전체」는 일곱 상태 어느 것도 아니다. 상태 팔레트(100 계열 파스텔)와 섞이지 않게 한 단 진한
// 회색을 쓴다 — 생성됨(gray-100)·종료(gray-100)와 같은 값이면 그 둘의 동료로 읽힌다.
const CHIP_ALL_CLS = 'bg-gray-200 text-gray-700';

function ProjectListPanel({ compact = false, selected = null, onSelect }) {
  const { getAccessToken } = useAuth();
  const { isMobileView } = useDesktopExpanded();
  const narrow = compact || isMobileView;

  const [projects, setProjects] = useState([]); // 전량(상한까지)
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  // 'all' = 거르지 않음. null 을 쓰지 않는다 — 상태 값이 없는 프로젝트의 칩도 null 로 묶여
  // 그 칩을 누르면 「전체」와 같은 뜻이 돼 버린다(같은 값이 두 가지를 뜻하게 된다).
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const fetchingRef = useRef(false);
  const refreshQueuedRef = useRef(false); // fetch 진행 중 갱신 요청(생성 직후) 드롭 방지 — 완료 후 1회 재조회

  const calculateItemsPerPage = useCallback(() => {
    try {
      const windowHeight = window.innerHeight;
      const cardHeight = isMobileView ? windowHeight - 50 - 68 - 29 : windowHeight - 96;
      const fixed = 64 + 40 + 60 + 20; // 헤더 + 테이블헤더 + 페이지네이션 + 여백
      const avail = cardHeight - fixed;
      // 행높이 56px: 프로젝트명 + 키워드 칩 줄(단일행 클립)의 2행 구성 기준(WorkListTab 44px 단일행 관례의 보정)
      const count = Math.floor(avail / 56);
      return Math.max(5, Math.min(count, 100)); // 계약 per_page 5~100
    } catch (e) {
      return 20;
    }
  }, [isMobileView]);

  // 전량을 한 번에 받는다. 프로젝트는 「책 한 권」 단위라 수백을 넘지 않고, 100건당 왕복 1회다.
  const fetchProjects = useCallback(async () => {
    if (fetchingRef.current) {
      refreshQueuedRef.current = true; // 진행 중이면 완료 후 1회 재조회로 흡수
      return;
    }
    fetchingRef.current = true;
    setLoading(true);
    try {
      const acc = [];
      let pageToLoad = 1;
      let serverTotalPages = 1;
      let cut = false;
      do {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchOcrProjects({ page: pageToLoad, perPage: FETCH_PAGE_SIZE }, getAccessToken);
        acc.push(...data.items);
        serverTotalPages = data.totalPages || 1;
        pageToLoad += 1;
        if (pageToLoad > FETCH_MAX_PAGES) { cut = serverTotalPages >= pageToLoad; break; }
      } while (pageToLoad <= serverTotalPages);
      setProjects(acc);
      setTruncated(cut);
    } catch (e) {
      // 백엔드 미배포 동안의 네트워크/서버 에러 → 빈 목록으로 흡수(콘솔 추가 로그 없음)
      setProjects([]);
      setTruncated(false);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        fetchProjects();
      }
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 상태별 건수 — 배지가 이 수를 그대로 말한다. 있는 상태만 칩으로 낸다(0건 칩은 소음이다).
  const statusCounts = useMemo(() => {
    const m = new Map();
    projects.forEach((p) => { const k = p.status ?? null; m.set(k, (m.get(k) ?? 0) + 1); });
    return [...m.entries()]
      .sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0)) // 파이프라인 순서(100 생성됨 → 500 종료)
      .map(([status, count]) => ({ status, count, ...getProjectStatusDisplay(status) }));
  }, [projects]);

  const filtered = useMemo(
    () => (statusFilter === 'all' ? projects : projects.filter((p) => (p.status ?? null) === statusFilter)),
    [projects, statusFilter],
  );

  const perPage = calculateItemsPerPage();
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  // 항목이 줄어 현재 페이지가 사라졌으면 마지막 페이지로 당긴다(빈 화면에 갇히지 않는다).
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const isRowSelected = (projectId) => selected != null && String(selected) === String(projectId);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 헤더: 타이틀 + 새 프로젝트 */}
      <div className="flex shrink-0 items-center justify-between border-b p-4">
        <h2 className={`font-bold text-gray-900 ${narrow ? 'text-sm' : 'text-base'}`}>OCR 프로젝트</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)} icon={<Plus className="size-4" />}>
          새 프로젝트
        </Button>
      </div>

      {/* 새 프로젝트 생성 모달 — 성공 시 목록 1페이지 재조회 + 생성 프로젝트 상세로 이동 */}
      <ProjectCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(project) => {
          setCreateOpen(false);
          fetchProjects();
          setStatusFilter('all'); // 새 프로젝트가 필터에 걸려 안 보이는 일이 없게 한다
          setPage(1);
          const newId = project?.project_id ?? project?.id;
          if (newId != null && onSelect) onSelect(newId);
        }}
      />

      {/* 상태 필터 — 목록 위에 두되 스크롤 밖에 둔다. 안에 넣으면 표 머리(sticky top-0)와 자리를 다툰다.
          칩 모양·색은 같은 메뉴(OCR)의 문제 목록 필터(OcrProblemList.jsx)를 그대로 쓴다 — 한 제품에서
          같은 일을 하는 줄이 두 얼굴이면 다른 것으로 읽힌다. */}
      {!loading && projects.length > 0 && (
        <div className="shrink-0 px-4 pt-2">
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => { setStatusFilter('all'); setPage(1); }}
              className={`rounded-full px-3 py-1 text-xs transition-colors ${CHIP_ALL_CLS} ${
                statusFilter === 'all' ? CHIP_ON : CHIP_OFF
              }`}
            >
              전체 {projects.length}
            </button>
            {statusCounts.map((sc) => (
              <button
                key={sc.status ?? 'unknown'}
                type="button"
                onClick={() => { setStatusFilter(sc.status); setPage(1); }}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${sc.cls} ${
                  statusFilter === sc.status ? CHIP_ON : CHIP_OFF
                }`}
              >
                {sc.text} {sc.count}
              </button>
            ))}
          </div>
          {/* 상한에서 끊겼으면 말한다 — 배지의 수가 전부가 아니게 되므로 조용히 넘어가면 안 된다 */}
          {truncated && (
            <p className="mt-1 text-[10px] text-amber-600">
              {FETCH_PAGE_SIZE * FETCH_MAX_PAGES}건까지만 읽었습니다 — 그 뒤 프로젝트는 이 수에 들어 있지 않습니다.
            </p>
          )}
        </div>
      )}

      {/* 목록 — px-4는 헤더/상세패널의 카드 안쪽 여백(p-4)과 같은 좌우 기준선. 선택 행의 rounded-3xl이 카드 모서리에 붙지 않는다 */}
      <div className="mt-2 min-h-0 flex-1 overflow-auto px-4">
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-500">로딩 중...</div>
        ) : pageItems.length > 0 ? (
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">프로젝트</th>
                {!narrow && (
                  <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">파일·페이지</th>
                )}
                <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">상태</th>
                {!narrow && (
                  <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">생성일</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white">
              {pageItems.map((p) => {
                const statusInfo = getProjectStatusDisplay(p.status, p.status_label);
                const rowSel = isRowSelected(p.project_id);
                const closed = (p.status ?? 0) >= 500;
                return (
                  <tr
                    key={p.project_id}
                    onClick={() => onSelect && onSelect(p.project_id)}
                    className={`cursor-pointer border-b border-gray-100 transition-colors duration-150 ${rowSel ? 'bg-primary/15' : 'hover:bg-primary/10'}`}
                  >
                    <td className="rounded-l-3xl px-3 py-2">
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span className={`shrink-0 font-mono text-xs font-bold ${closed ? 'text-gray-900' : 'text-primary'}`}>
                          {p.project_id}
                        </span>
                        <div className={`min-w-0 truncate text-sm ${rowSel ? 'font-bold text-primary' : 'text-gray-900'}`}>
                          {p.name || '프로젝트'}
                        </div>
                      </div>
                      {Array.isArray(p.keywords) && p.keywords.length > 0 && (
                        // 단일행 클립(줄바꿈 없음) — 행높이를 결정적으로 유지(per_page 56px 가정과 짝)
                        <div className="mt-0.5 flex gap-1 overflow-hidden">
                          {p.keywords.map((kw) => (
                            <span key={kw} className="shrink-0 rounded-full bg-gray-100 px-1.5 py-px text-[10px] text-gray-500">
                              {kw}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {!narrow && (
                      <td className="whitespace-nowrap px-3 py-2 text-center text-sm text-gray-700">
                        {p.file_count ?? 0}개 · {p.total_pages ?? 0}p
                      </td>
                    )}
                    <td className={`whitespace-nowrap px-3 py-2 text-center ${narrow ? 'rounded-r-3xl' : ''}`}>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.cls}`}>
                        {statusInfo.text}
                      </span>
                    </td>
                    {!narrow && (
                      <td className="whitespace-nowrap rounded-r-3xl px-3 py-2 text-center text-sm text-gray-500">
                        <V2DateTime value={p.created_at} format="date" fallback="—" />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex h-full items-center justify-center">
            {statusFilter !== 'all' ? (
              <EmptyState
                message={`「${getProjectStatusDisplay(statusFilter).text}」 상태의 프로젝트가 없습니다.`}
                description="위에서 다른 상태를 고르거나 [전체]를 누르세요."
              />
            ) : (
              <EmptyState message="프로젝트가 없습니다." description="새 프로젝트를 생성하세요." />
            )}
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {!loading && totalItems > 0 && totalPages > 1 && (
        <div className="shrink-0 bg-white pb-4">
          <DesktopPagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage)}
            itemsPerPage={perPage}
            totalItems={totalItems}
            showInfo={!narrow}
          />
        </div>
      )}
    </div>
  );
}

export default ProjectListPanel;
